/**
 * Course notification document references for Firebase client SDK.
 *
 * Uses a client schema with ClientTimestampSchema so timestamp (and expiresAt)
 * are typed as Firestore Timestamp; call .toDate() where you display them.
 */
import type {CollectionReference, DocumentReference} from 'firebase/firestore';
import {collection, doc, getFirestore} from 'firebase/firestore';
import {z} from 'zod';

import {ClientTimestampSchema, createConverter} from './sdk';
import {
  getCourseNotificationCollectionPath,
  getCourseNotificationDocumentPath,
  ForumCourseNotificationSchema,
  PostSectionSurveyCourseNotificationSchema,
  ShadowFeedbackReceivedCourseNotificationSchema,
  ShadowMomentNominatedCourseNotificationSchema,
  ShadowReplyCourseNotificationSchema,
  ShadowReviewDueCourseNotificationSchema,
  TeachNowStudentCourseNotificationSchema,
} from '../documents/notification';

// ============================================================================
// Client schema (timestamp/expiresAt as Timestamp for .toDate())
// ============================================================================

const ClientForumCourseNotificationSchema = ForumCourseNotificationSchema.omit({
  timestamp: true,
}).extend({timestamp: ClientTimestampSchema});

const ClientPostSectionSurveyCourseNotificationSchema =
  PostSectionSurveyCourseNotificationSchema.omit({
    timestamp: true,
    expiresAt: true,
  }).extend({
    timestamp: ClientTimestampSchema,
    expiresAt: z.union([ClientTimestampSchema, z.null()]).optional(),
  });

const ClientTeachNowStudentCourseNotificationSchema =
  TeachNowStudentCourseNotificationSchema.omit({timestamp: true}).extend({
    timestamp: ClientTimestampSchema,
  });

const ClientShadowMomentNominatedCourseNotificationSchema =
  ShadowMomentNominatedCourseNotificationSchema.omit({timestamp: true}).extend({
    timestamp: ClientTimestampSchema,
  });

const ClientShadowReplyCourseNotificationSchema =
  ShadowReplyCourseNotificationSchema.omit({timestamp: true}).extend({
    timestamp: ClientTimestampSchema,
  });

const ClientShadowFeedbackReceivedCourseNotificationSchema =
  ShadowFeedbackReceivedCourseNotificationSchema.omit({timestamp: true}).extend(
    {
      timestamp: ClientTimestampSchema,
    },
  );

const ClientShadowReviewDueCourseNotificationSchema =
  ShadowReviewDueCourseNotificationSchema.omit({timestamp: true}).extend({
    timestamp: ClientTimestampSchema,
  });

const CourseNotificationSchema = z.discriminatedUnion('type', [
  ClientForumCourseNotificationSchema,
  ClientPostSectionSurveyCourseNotificationSchema,
  ClientTeachNowStudentCourseNotificationSchema,
  ClientShadowMomentNominatedCourseNotificationSchema,
  ClientShadowReplyCourseNotificationSchema,
  ClientShadowFeedbackReceivedCourseNotificationSchema,
  ClientShadowReviewDueCourseNotificationSchema,
]);

export type CourseNotification = z.infer<typeof CourseNotificationSchema>;

const courseNotificationConverter = createConverter<CourseNotification>({
  schema: CourseNotificationSchema,
  options: {validateOnRead: false, validateOnWrite: false},
});

export function getCourseNotificationCollectionRef({
  userId,
}: {
  userId: string;
}): CollectionReference<CourseNotification> {
  return collection(
    getFirestore(),
    getCourseNotificationCollectionPath(userId),
  ).withConverter(courseNotificationConverter);
}

export function getCourseNotificationDocRef({
  userId,
  notificationId,
}: {
  userId: string;
  notificationId: string;
}): DocumentReference<CourseNotification> {
  return doc(
    getFirestore(),
    getCourseNotificationDocumentPath({userId, notificationId}),
  ).withConverter(courseNotificationConverter);
}
